import { forwardRef, useId, type SelectHTMLAttributes } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@shared/lib/cn';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  /** Groups the option under a heading in the list. */
  group?: string;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  label: string;
  options: SelectOption[];
  error?: string;
  hint?: string;
  /** Leading blank option, for a field with no sensible default. */
  placeholder?: string;
}

/**
 * A native `<select>` behind the app's field styling, wired to its label, hint
 * and error the same way `Input` is.
 *
 * Native rather than a custom listbox: the option lists here are short, and the
 * platform control brings keyboard behaviour and mobile pickers for free.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, options, error, hint, placeholder, className, id, ...props },
  ref,
) {
  const generatedId = useId();
  const selectId = id ?? generatedId;
  const errorId = `${selectId}-error`;
  const hintId = `${selectId}-hint`;

  // Preserves the given order while collecting each group's options together.
  const groups = options.reduce<Array<{ name: string | undefined; options: SelectOption[] }>>(
    (accumulator, option) => {
      const last = accumulator[accumulator.length - 1];

      if (last && last.name === option.group) {
        last.options.push(option);
      } else {
        accumulator.push({ name: option.group, options: [option] });
      }

      return accumulator;
    },
    [],
  );

  return (
    <div className="space-y-1.5">
      <label htmlFor={selectId} className="block text-sm font-medium text-slate-700">
        {label}
      </label>

      <div className="relative">
        <select
          ref={ref}
          id={selectId}
          aria-invalid={error ? true : undefined}
          aria-describedby={cn(error && errorId, hint && hintId) || undefined}
          className={cn(
            'block w-full appearance-none rounded-md border bg-white py-2 pl-3 pr-9 text-sm text-slate-900',
            'disabled:bg-slate-50 disabled:text-slate-500',
            error ? 'border-red-500' : 'border-slate-300',
            className,
          )}
          {...props}
        >
          {placeholder && <option value="">{placeholder}</option>}

          {groups.map((group, index) =>
            group.name ? (
              <optgroup key={group.name} label={group.name}>
                {group.options.map((option) => (
                  <option key={option.value} value={option.value} disabled={option.disabled}>
                    {option.label}
                  </option>
                ))}
              </optgroup>
            ) : (
              group.options.map((option) => (
                <option
                  key={option.value || `blank-${index}`}
                  value={option.value}
                  disabled={option.disabled}
                >
                  {option.label}
                </option>
              ))
            ),
          )}
        </select>

        <ChevronDown
          aria-hidden
          className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400"
        />
      </div>

      {hint && !error && (
        <p id={hintId} className="text-xs text-slate-500">
          {hint}
        </p>
      )}

      {error && (
        <p id={errorId} role="alert" className="text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
});
